/**
 * The complexity-adjustment recurrence ("FlowBot") — pure, side-effect free.
 *
 * This is a faithful re-implementation of `ComplexityOracle.xlsx`, which is the
 * source of truth. Every branch below maps to a clause of the sheet's column-C
 * formula; see `documentation/complexity-oracle-findings.md` for the
 * full decomposition and the conflicts with the prose specs.
 *
 * Design notes:
 *
 * - The trajectory is recomputed from the whole verdict history on every call
 *   rather than accumulated incrementally. Sessions are tens of phrases, so the
 *   cost is irrelevant, and it removes an entire class of drift bugs — the
 *   spreadsheet is itself a full-column recompute, so this also keeps the two
 *   structurally identical.
 * - Nothing here mutates its inputs.
 * - Indices are 1-based to match the oracle and the engineering spec's
 *   `AssessmentPhrase.idx`. Slot 0 stands in for the sheet's `"CR Min"` text
 *   header, which occupies a position in the plateau window but never matches
 *   numerically.
 */

import { AssessmentBand, DEFAULT_TIER_CONFIG, TierConfig, Verdict } from './complexityModel.types';

/** Width of the plateau window, in floors. Oracle: `$C[r-4]:$C[r-1]`.
 *
 *  ⚠️ The engineering spec says "the last three floors" and "the last (5 - PAD)
 *  floors". Both are wrong — the sheet's range is a fixed four cells regardless
 *  of PAD. See findings §2, Conflict 3. */
const PLATEAU_WINDOW = 4;

/** Half-width of the "unchanged" tolerance used by the plateau test, expressed
 *  as `cvr / 2 + PLATEAU_EPSILON`. The `0.01` is literal in the oracle. */
const PLATEAU_EPSILON = 0.01;

/** Tolerance for treating two floors as equal. Floors are sums of `cai`/`cdi`
 *  multiples, so binary-float residue (e.g. `0.30000000000000004`) must not be
 *  read as a real difference. */
const EPSILON = 1e-12;

/** A plateau-window slot. `null` represents the sheet's text header cell — it
 *  holds a position but can never satisfy a numeric comparison. */
type WindowSlot = number | null;

/**
 * `XMATCH(target, window, match_mode, search_mode = -1)` restricted to the two
 * modes the oracle uses, returning a 1-based position or 0 when nothing
 * qualifies (the sheet wraps both calls in `IFERROR(..., 0)`).
 *
 * `preferLarger = false` is Excel's `match_mode -1` (exact or next smaller);
 * `true` is `match_mode +1` (exact or next larger). Both use `search_mode -1`
 * (last-to-first), so ties resolve to the LAST qualifying position.
 */
function xmatch(target: number, window: readonly WindowSlot[], preferLarger: boolean): number {
  const qualifying = window
    .map((value, position) => ({ value, position: position + 1 }))
    .filter(
      (slot): slot is { value: number; position: number } =>
        slot.value !== null &&
        (preferLarger ? slot.value >= target - EPSILON : slot.value <= target + EPSILON),
    );
  if (qualifying.length === 0) return 0;

  const best = preferLarger
    ? Math.min(...qualifying.map((slot) => slot.value))
    : Math.max(...qualifying.map((slot) => slot.value));

  return Math.max(
    ...qualifying
      .filter((slot) => Math.abs(slot.value - best) < EPSILON)
      .map((slot) => slot.position),
  );
}

/**
 * The oracle's `PLATEAU` expression, evaluated for the row that computes
 * assessment phrase `idx`.
 *
 * Despite the name this is NOT stuck-detection — it is an anti-jitter
 * **cooldown**. `xmatch` returns a *position*, not a count, and the window's
 * last slot is the current floor itself (always in-band). So
 * `plateau >= 5 - pad` is true exactly when a recent floor sits outside
 * ±(cvr/2 + 0.01) of the current one — i.e. when the band moved on the previous
 * step. The effect is that the band advances `move → hold → bigger move → hold`.
 *
 * ⚠️ The engineering spec paraphrases this as the *opposite* condition
 * ("if the last floors are all within ±CVR/2 ... suppress this step"), which
 * yields a model that never advances. See findings §3.
 */
function cooldownPosition(
  floors: ReadonlyMap<number, number>,
  idx: number,
  config: TierConfig,
): number {
  const current = floors.get(idx - 1);
  if (current === undefined) return 0;

  // Window is floors[idx-4 .. idx-1], clipped at slot 0 (the sheet's header
  // cell) exactly as the oracle's range is clipped at `$C2` for early rows.
  const start = Math.max(0, idx - PLATEAU_WINDOW);
  const window: WindowSlot[] = [];
  for (let slot = start; slot <= idx - 1; slot += 1) {
    window.push(floors.get(slot) ?? null);
  }

  const tolerance = config.cvr / 2 + PLATEAU_EPSILON;
  return Math.max(
    xmatch(current - tolerance, window, false),
    xmatch(current + tolerance, window, true),
  );
}

/** True when the cooldown suppresses this step. Oracle: `PLATEAU >= 5 - PAD`. */
function isCoolingDown(cooldown: number, config: TierConfig): boolean {
  return cooldown >= 5 - config.pad;
}

/**
 * Green step. Oracle: the `$B[r-2] = "G"` branch.
 *
 * `delta` is the previously *applied* change (`floor[p-2] - floor[p-3]`), and
 * `priorDriver` is `verdict[p-3]`. A lone green nudges up by `cai`; a sustained
 * green streak compounds by carrying `delta` forward through the geometric term
 * and adding a `cai * aim` kicker.
 *
 * `delta === undefined` reproduces the sheet's `IFERROR` fallbacks on the first
 * computed row, where `C3 - C2` is text arithmetic: geometric → `cai`,
 * arithmetic → 0.
 */
function stepUp(
  previous: number,
  delta: number | undefined,
  priorDriver: Verdict | undefined,
  config: TierConfig,
): number {
  const { cai, gim, aim, cvr } = config;
  if (delta === undefined) return Math.min(previous + cai, 1 - cvr);

  const isFlat = Math.abs(delta) < EPSILON;
  const geometric =
    isFlat || priorDriver !== Verdict.Green ? 0 : (delta < 0 ? cai / gim : Math.abs(delta)) * gim;

  let arithmetic: number;
  if (isFlat) arithmetic = cai;
  else if (delta < 0) arithmetic = priorDriver !== Verdict.Green ? cai : 0;
  else arithmetic = priorDriver === Verdict.Yellow ? cai : cai * aim;

  return Math.min(previous + geometric + arithmetic, 1 - cvr);
}

/**
 * Red step. Oracle: the `$B[r-2] = "R"` branch — symmetric to green but
 * downward and clamped at 0.
 *
 * The sheet splits on `C4 > C3` (`wasRising`), which guards against a red
 * verdict raising the band. When the cooldown is active AND the band was
 * rising, the sheet still steps down but via a slightly different term pair;
 * when the cooldown is active and it was NOT rising, it holds (handled by the
 * caller).
 */
function stepDown(
  previous: number,
  delta: number | undefined,
  priorDriver: Verdict | undefined,
  wasRising: boolean,
  coolingDown: boolean,
  config: TierConfig,
): number {
  const { cdi, gim, aim } = config;
  if (delta === undefined) return Math.max(previous - cdi, 0);

  const isFlat = Math.abs(delta) < EPSILON;
  let geometric: number;
  let arithmetic: number;

  if (coolingDown && wasRising) {
    geometric =
      isFlat || priorDriver !== Verdict.Red ? 0 : (delta > 0 ? cdi / gim : Math.abs(delta)) * gim;
    // The sheet's outer `IF(<term>, ...)` is a truthiness test: a zero delta
    // substitutes `cdi` (truthy), so only a genuine zero suppresses the term.
    const guard = isFlat ? cdi : delta;
    arithmetic = guard === 0 ? 0 : delta >= 0 ? cdi : cdi * aim;
  } else {
    geometric = isFlat
      ? 0
      : (delta > 0 ? cdi / gim : priorDriver !== Verdict.Red ? 0 : Math.abs(delta)) * gim;

    if (isFlat) arithmetic = cdi;
    else if (delta > 0) arithmetic = priorDriver === Verdict.Red ? cdi : 0;
    else arithmetic = priorDriver !== Verdict.Red ? cdi : cdi * aim;
  }

  return Math.max(previous - geometric - arithmetic, 0);
}

/**
 * Compute the band trajectory for an assessment stream.
 *
 * @param verdicts  Verdicts in assessment order. `verdicts[i]` is the verdict
 *                  of assessment phrase `i + 1`. Not mutated.
 * @param config    Per-tier tunables.
 * @param through   Highest assessment index to compute. Defaults to
 *                  `verdicts.length + config.pad`, i.e. far enough to cover the
 *                  phrase currently being requested — the whole point of PAD is
 *                  that the band for phrase `p` is knowable once phrase
 *                  `p - pad` has been graded.
 *
 * @returns One {@link AssessmentBand} per assessment phrase, 1-based order.
 */
export function computeTrajectory(
  verdicts: readonly Verdict[],
  config: TierConfig = DEFAULT_TIER_CONFIG,
  through: number = verdicts.length + config.pad,
): AssessmentBand[] {
  if (config.gim === 0) throw new Error('TierConfig.gim must be non-zero (used as a divisor)');
  if (config.pad < 1) throw new Error('TierConfig.pad must be >= 1');
  if (through < 0) throw new Error('`through` must be non-negative');

  const floors = new Map<number, number>();
  const bands: AssessmentBand[] = [];
  /** Verdict of assessment phrase `p`, or undefined if not yet graded. */
  const verdictAt = (p: number): Verdict | undefined =>
    p >= 1 && p <= verdicts.length ? verdicts[p - 1] : undefined;

  for (let idx = 1; idx <= through; idx += 1) {
    const cooldown = cooldownPosition(floors, idx, config);
    let crMin: number;
    let driver: Verdict | undefined;

    if (idx <= 2) {
      // Seeds: CR_Min[1] = complexity_start, CR_Min[2] = CR_Min[1].
      crMin = config.complexityStart;
    } else {
      driver = verdictAt(idx - config.pad);
      const previous = floors.get(idx - 1) ?? config.complexityStart;
      const twoBack = floors.get(idx - 2);
      const threeBack = floors.get(idx - 3);
      const delta =
        twoBack !== undefined && threeBack !== undefined ? twoBack - threeBack : undefined;
      const priorDriver = verdictAt(idx - 3);
      const coolingDown = isCoolingDown(cooldown, config);

      if (driver === Verdict.Green) {
        crMin = coolingDown ? previous : stepUp(previous, delta, priorDriver, config);
      } else if (driver === Verdict.Red) {
        const wasRising = twoBack !== undefined && previous > twoBack;
        crMin =
          coolingDown && !wasRising
            ? previous
            : stepDown(previous, delta, priorDriver, wasRising, coolingDown, config);
      } else {
        // Yellow, or no driver yet (ungraded — the band simply holds).
        //
        // Yellow is an unconditional hold — a DELIBERATE deviation from the
        // oracle, whose non-cooldown yellow branch is `INDEX(...) ->
        // floor[p - pad]`.
        //
        // Under the default preset the two are indistinguishable: zero
        // divergence over all 3^9 verdict sequences and over 3,000 random
        // sequences of length 20-200. But they are NOT equivalent in general.
        // `PLATEAU` resolves to the position of the *closest* out-of-band floor,
        // not of any out-of-band floor, so a nearer out-of-band value at window
        // position 1-2 can mask a large gap at position 3 and leave the cooldown
        // inactive. With Streak Boost OFF that happens in ~7% of sequences, and
        // the oracle's INDEX then jumps the band by up to 0.3 — on a verdict
        // whose entire purpose is to hold. At pad = 3 (mobile) the INDEX also
        // reads out of bounds into the sheet's text header.
        //
        // Holding is what both spec documents mandate ("Yellow never moves the
        // band"; "must hold"), and the divergence is the oracle misbehaving
        // rather than the hold drifting. See findings §4, Conflict 4.
        crMin = previous;
      }
    }

    floors.set(idx, crMin);
    bands.push({
      idx,
      crMin,
      crMax: Math.min(crMin + config.cvr, 1),
      driver,
      cooldown,
    });
  }

  return bands;
}

/**
 * The band in effect for a single assessment phrase — a convenience wrapper
 * over {@link computeTrajectory} for the request path, where the caller needs
 * the band for the phrase it is about to fetch.
 */
export function bandFor(
  assessmentIndex: number,
  verdicts: readonly Verdict[],
  config: TierConfig = DEFAULT_TIER_CONFIG,
): AssessmentBand {
  if (assessmentIndex < 1) throw new Error('assessmentIndex is 1-based');
  const trajectory = computeTrajectory(verdicts, config, assessmentIndex);
  const band = trajectory[assessmentIndex - 1];
  if (band === undefined) throw new Error(`no band computed for index ${assessmentIndex}`);
  return band;
}

/**
 * Classify a phrase's complexity error tally into a verdict.
 *
 * `errorTotal` uses the complexity scale — red note 1.0, yellow note 0.5 —
 * which is deliberately NOT `EventStream`'s accuracy scale (semi-error 0.7).
 * The oracle's thresholds were calibrated against 0.5.
 */
export function classify(errorTotal: number, config: TierConfig = DEFAULT_TIER_CONFIG): Verdict {
  if (errorTotal < 0) throw new Error('errorTotal must be non-negative');
  if (errorTotal <= config.greenMax) return Verdict.Green;
  if (errorTotal <= config.yellowMax) return Verdict.Yellow;
  return Verdict.Red;
}
